import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { COOKIE_CONSENT_POLICY_VERSION } from '../constants/CookieConsent';
import {
  clearNonEssentialStorage,
  getStoredConsent,
  hasValidConsent,
  saveStoredConsent,
} from '../utils/cookieConsentStorage';
import { initGoogleAdsTag, syncGoogleAdsConsent } from '../utils/googleAdsTag';

export const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => getStoredConsent());
  const [bannerOpen, setBannerOpen] = useState(() => !hasValidConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const persistConsent = useCallback((preferences) => {
    const previous = getStoredConsent();
    const savedConsent = saveStoredConsent(preferences);

    if (previous?.functional && !preferences.functional) {
      clearNonEssentialStorage();
    }

    syncGoogleAdsConsent(savedConsent.advertising === true);
    setConsent(savedConsent);
    setBannerOpen(false);
    setPreferencesOpen(false);
    return savedConsent;
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent({ functional: true, thirdParty: true, advertising: true });
  }, [persistConsent]);

  const rejectNonEssential = useCallback(() => {
    persistConsent({ functional: false, thirdParty: false, advertising: false });
  }, [persistConsent]);

  const savePreferences = useCallback((preferences) => {
    persistConsent(preferences);
  }, [persistConsent]);

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
    setBannerOpen(false);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
    if (!hasValidConsent()) {
      setBannerOpen(true);
    }
  }, []);

  const isConsentRequired = !consent?.timestamp || consent.version !== COOKIE_CONSENT_POLICY_VERSION;

  useEffect(() => {
    initGoogleAdsTag();
    syncGoogleAdsConsent(consent?.advertising === true);
  }, [consent?.advertising]);

  useEffect(() => {
    if (!isConsentRequired) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isConsentRequired]);

  const value = useMemo(() => ({
    consent,
    bannerOpen,
    preferencesOpen,
    isConsentRequired,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
    setBannerOpen,
  }), [
    acceptAll,
    bannerOpen,
    closePreferences,
    consent,
    isConsentRequired,
    openPreferences,
    preferencesOpen,
    rejectNonEssential,
    savePreferences,
  ]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}
